import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import VeryfitLoader from "./VeryfitLoader";

const AuthWrapper = ({ children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("🔄 AuthWrapper - État auth changé:", user ? "connecté" : "déconnecté");
      
      const currentPath = window.location.pathname;
      const isInCreateAccountPage = currentPath.includes("/fit/fit-create-account");
      const isOnHomePage = currentPath === "/";
      const publicPaths = ["/", "/create-account", "/forgot-password", "/reset-password", "/unauthorized", "/confidentialite"];
      const isOnPublicPath = publicPaths.includes(currentPath);

      if (!user) {
        console.log("❌ Pas d'utilisateur connecté");
        if (isOnPublicPath || isInCreateAccountPage) {
          setLoading(false);
          setAuthChecked(true);
          return;
        }
        navigate("/", { replace: true });
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      console.log("✅ Utilisateur connecté:", user.uid);

      let claims = null;
      let hasRole = false;
      let userData = null;

      // 1. Essayer d'abord les custom claims
      for (let i = 0; i < 3; i++) {
        try {
          const result = await user.getIdTokenResult(true);
          claims = result.claims || {};
          if (claims.role) {
            hasRole = true;
            console.log("✅ Rôle trouvé:", claims.role);
            break;
          }
        } catch (err) {
          console.error("❌ Erreur claims tentative", i + 1, ":", err);
        }
        if (i < 2) await new Promise((res) => setTimeout(res, 500));
      }

      // 2. Si pas de claims, essayer l'API backend (au lieu de Firestore direct)
      if (!hasRole) {
        try {
          console.log("🔄 Fallback API backend...");
          
          // Utiliser l'API backend pour récupérer les données utilisateur
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/get-user-data`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await user.getIdToken()}`
            },
            body: JSON.stringify({ uid: user.uid })
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.userData) {
              userData = result.userData;
              claims = {
                role: userData.role,
                isApproved: userData.isApproved === true,
              };
              hasRole = !!userData.role;
              console.log("✅ Rôle via API:", userData.role);
              console.log("✅ Données utilisateur:", userData);
            }
          } else {
            console.warn("⚠️ API utilisateur non disponible, fallback Firestore");
            
            // Fallback Firestore seulement si l'API échoue
            try {
              const { doc, getDoc } = await import("firebase/firestore");
              const { db } = await import("../firebaseConfig");
              
              const userDoc = await getDoc(doc(db, "users_webapp", user.uid));
              if (userDoc.exists()) {
                userData = userDoc.data();
                claims = {
                  role: userData.role,
                  isApproved: userData.isApproved === true,
                };
                hasRole = !!userData.role;
                console.log("✅ Rôle Firestore (fallback):", userData.role);
              }
            } catch (firestoreErr) {
              console.error("❌ Erreur Firestore fallback:", firestoreErr);
            }
          }
        } catch (err) {
          console.error("❌ Erreur récupération données utilisateur:", err);
        }
      }

      if (!hasRole) {
        console.warn("❌ Aucun rôle disponible");
        if (!isInCreateAccountPage) {
          navigate("/unauthorized", { replace: true });
        }
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      // 3. Vérifier l'approbation (logique assouplie pour FIT et clients)
      const isApprovedByFit = ["FIT", "fit"].includes(userData?.createdBy);
      const isDirectlyApproved = claims.isApproved === true || userData?.approved === true;
      const isFitRole = ["fit", "FIT", "Super Admin", "super admin"].includes(claims.role);
      const isClientRole = ["Client", "client", "Client Final", "client final"].includes(claims.role);
      const isUserApproved = isDirectlyApproved || isApprovedByFit || isFitRole || isClientRole;

      console.log("🔍 Vérification approbation:", {
        isDirectlyApproved,
        isApprovedByFit,
        isFitRole,
        isClientRole,
        isUserApproved,
        role: claims.role,
        createdBy: userData?.createdBy,
        approved: userData?.approved
      });

      if (!isUserApproved) {
        console.warn("⛔ Utilisateur non approuvé");
        if (!isInCreateAccountPage) {
          navigate("/unauthorized", { replace: true });
        }
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      // 🔥 MISE À JOUR : Mappings avec Client Final
      const roleToBasePath = {
        "Super Admin": "/fit",
        "super admin": "/fit",
        "fit": "/fit",
        "FIT": "/fit",
        "Revendeur": "/revendeur",
        "revendeur": "/revendeur",
        "Carrossier": "/carrossier",
        "carrossier": "/carrossier",
        "Utilisateur": "/client",
        "utilisateur": "/client",
        "Client": "/client",
        "client": "/client",
        "Client Final": "/client",  // 🔥 AJOUTÉ
        "client final": "/client",  // 🔥 AJOUTÉ
      };

      const roleToDefaultDashboard = {
        "Super Admin": "/fit/dashboard",
        "super admin": "/fit/dashboard",
        "fit": "/fit/dashboard",
        "FIT": "/fit/dashboard",
        "Revendeur": "/revendeur/dashboard",
        "revendeur": "/revendeur/dashboard",
        "Carrossier": "/carrossier/dashboard",
        "carrossier": "/carrossier/dashboard",
        "Utilisateur": "/client/dashboard",
        "utilisateur": "/client/dashboard",
        "Client": "/client/dashboard",
        "client": "/client/dashboard",
        "Client Final": "/client/dashboard",  // 🔥 AJOUTÉ
        "client final": "/client/dashboard",  // 🔥 AJOUTÉ
      };

      const userBasePath = roleToBasePath[claims.role];
      const userDefaultDashboard = roleToDefaultDashboard[claims.role];

      const isOnAuthorizedPath = userBasePath && currentPath.startsWith(userBasePath);

      console.log("📍 currentPath:", currentPath);
      console.log("🎯 userBasePath:", userBasePath);
      console.log("✅ isOnAuthorizedPath:", isOnAuthorizedPath);
      console.log("👤 Rôle utilisateur:", claims.role);

      if (!userBasePath) {
        console.error("❌ Rôle non mappé:", claims.role);
        navigate("/unauthorized", { replace: true });
      } else if (isOnHomePage || (!isOnAuthorizedPath && !isInCreateAccountPage)) {
        console.log("➡️ Redirection vers:", userDefaultDashboard);
        navigate(userDefaultDashboard, { replace: true });
      } else {
        console.log("✅ Utilisateur sur la bonne page, pas de redirection");
      }

      setLoading(false);
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, [navigate]);

  if (loading || !authChecked) {
    return <VeryfitLoader />;
  }

  return <>{children}</>;
};

export default AuthWrapper;